<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back to dashboard">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-bed me-2"></i>{{ t('ward.title','Wards & Beds') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-blue" @click="openAddWard">
            <i class="mdi mdi-plus me-1"></i>Add Ward
          </button>
          <button class="hms-btn hms-btn-primary" @click="openAddBed()" :disabled="!wards.length">
            <i class="mdi mdi-bed-empty me-1"></i>Add Bed
          </button>
        </div>
      </div>

      <!-- Stat row -->
      <div class="hms-stat-row" v-if="!loading">
        <div class="hms-stat">
          <div class="hms-stat-num">{{ totals.beds }}</div>
          <div class="hms-stat-lbl">Total Beds</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-green">{{ totals.available }}</div>
          <div class="hms-stat-lbl">Available</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-info">{{ totals.occupied }}</div>
          <div class="hms-stat-lbl">Occupied</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-amber">{{ totals.cleaning }}</div>
          <div class="hms-stat-lbl">Cleaning</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-purple">{{ activeAdmissions.length }}</div>
          <div class="hms-stat-lbl">Active Admissions</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab === 'map' }" @click="tab = 'map'">
          <i class="mdi mdi-view-grid me-1"></i>Bed Map
        </button>
        <button class="hms-tab" :class="{ active: tab === 'wards' }" @click="tab = 'wards'">
          <i class="mdi mdi-hospital-building me-1"></i>Wards
        </button>
        <button class="hms-tab" :class="{ active: tab === 'admissions' }" @click="tab = 'admissions'">
          <i class="mdi mdi-account-injury me-1"></i>Active Admissions
          <span class="hms-tab-count">{{ activeAdmissions.length }}</span>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="hms-spinner">
        <div class="spinner-border"></div>
      </div>

      <!-- ============ TAB: BED MAP ============ -->
      <div v-else-if="tab === 'map'">
        <div v-if="!groupedMap.length" class="hms-card hms-empty">
          <i class="mdi mdi-bed"></i>
          <div>No wards or beds yet. Click <strong>Add Ward</strong> to begin.</div>
        </div>

        <div v-for="grp in groupedMap" :key="grp.ward_id" class="hms-card hms-ward-block">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div>
              <div class="hms-ward-title">
                <i class="mdi mdi-hospital-building me-2"></i>{{ grp.ward_name }}
                <span class="hms-badge hms-badge-info ms-2">{{ formatWardType(grp.ward_type) }}</span>
                <span v-if="grp.gender_restriction && grp.gender_restriction !== 'mixed'" class="hms-badge hms-badge-neutral ms-1">
                  {{ formatGender(grp.gender_restriction) }}
                </span>
              </div>
              <div class="hms-muted-sm">
                Floor {{ grp.floor_number || 1 }} · {{ grp.beds.length }} beds ·
                <span class="hms-text-success">{{ grp.beds.filter(b => b.status === 'available').length }} available</span>
              </div>
            </div>
            <button class="hms-btn hms-btn-sm hms-btn-blue" @click="openAddBed(grp.ward_id)">
              <i class="mdi mdi-plus me-1"></i>Bed
            </button>
          </div>

          <div v-if="grp.beds.length" class="hms-bed-grid">
            <div
              v-for="bed in grp.beds"
              :key="bed.bed_id"
              class="hms-bed-cell"
              :class="bedClass(bed.status)"
              @click="openBedDetail(bed, grp)"
              :title="bed.status"
            >
              <div class="hms-bed-num">{{ bed.bed_number }}</div>
              <div class="hms-bed-status">{{ formatBedStatus(bed.status) }}</div>
              <div class="hms-bed-patient" v-if="bed.patient_name">
                <i class="mdi mdi-account me-1"></i>{{ bed.patient_name }}
              </div>
              <div class="hms-bed-icons">
                <i v-if="bed.has_oxygen == 1" class="mdi mdi-air-filter" title="Oxygen"></i>
                <i v-if="bed.has_ventilator == 1" class="mdi mdi-pulse" title="Ventilator"></i>
                <i v-if="bed.has_monitor == 1" class="mdi mdi-heart-pulse" title="Monitor"></i>
              </div>
            </div>
          </div>
          <div v-else class="hms-muted text-center py-3">
            No beds in this ward yet.
          </div>
        </div>
      </div>

      <!-- ============ TAB: WARDS ============ -->
      <div v-else-if="tab === 'wards'">
        <div class="hms-card">
          <div v-if="wards.length" style="overflow-x:auto;">
            <table class="hms-table">
              <thead><tr>
                <th>Ward</th>
                <th>Type</th>
                <th>Floor</th>
                <th>Beds</th>
                <th>Gender</th>
                <th>Status</th>
                <th>Actions</th>
              </tr></thead>
              <tbody>
                <tr v-for="w in wards" :key="w.ward_id">
                  <td>
                    <div class="hms-text fw-semibold">{{ w.ward_name }}</div>
                    <div class="hms-muted-sm" v-if="w.ward_code">{{ w.ward_code }}</div>
                  </td>
                  <td><span class="hms-badge hms-badge-info">{{ formatWardType(w.ward_type) }}</span></td>
                  <td>{{ w.floor_number || 1 }}</td>
                  <td>
                    <span class="hms-text">{{ w.total_beds }}</span>
                    <span class="hms-muted-sm">
                      ·
                      <span class="hms-text-success">{{ w.available_beds }} avail</span>
                      ·
                      <span class="hms-text-info">{{ w.occupied_beds }} occ</span>
                    </span>
                  </td>
                  <td>{{ formatGender(w.gender_restriction) }}</td>
                  <td><span class="hms-badge" :class="w.status === 'active' ? 'hms-badge-success' : 'hms-badge-warning'">{{ w.status }}</span></td>
                  <td>
                    <button class="hms-btn hms-btn-sm hms-btn-blue me-1" @click="openAddBed(w.ward_id)" title="Add bed">
                      <i class="mdi mdi-bed-empty"></i>
                    </button>
                    <button class="hms-btn hms-btn-sm hms-btn-amber" @click="openEditWard(w)" title="Edit">
                      <i class="mdi mdi-pencil"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-empty">
            <i class="mdi mdi-hospital-building"></i>No wards yet — click <strong>Add Ward</strong> to begin.
          </div>
        </div>
      </div>

      <!-- ============ TAB: ACTIVE ADMISSIONS ============ -->
      <div v-else-if="tab === 'admissions'">
        <div class="hms-card">
          <div v-if="activeAdmissions.length" style="overflow-x:auto;">
            <table class="hms-table">
              <thead><tr>
                <th>Patient</th>
                <th>Adm No</th>
                <th>Ward / Bed</th>
                <th>Type</th>
                <th>Doctor</th>
                <th>Admitted</th>
                <th>Days</th>
                <th>Actions</th>
              </tr></thead>
              <tbody>
                <tr v-for="a in activeAdmissions" :key="a.admission_id">
                  <td>
                    <div class="hms-text fw-semibold">{{ a.patient_name }}</div>
                    <div class="hms-mono hms-muted-sm">{{ a.patient_no }}</div>
                  </td>
                  <td><span class="hms-mono">{{ a.admission_number }}</span></td>
                  <td>
                    <div class="hms-text">{{ a.ward_name || '—' }}</div>
                    <div class="hms-muted-sm">Bed {{ a.bed_number || '—' }}</div>
                  </td>
                  <td><span class="hms-badge" :class="admTypeBadge(a.admission_type)">{{ a.admission_type }}</span></td>
                  <td>{{ a.doctor_name || '—' }}</td>
                  <td>{{ formatDate(a.admission_date) }}</td>
                  <td><strong>{{ a.days_admitted }}</strong></td>
                  <td>
                    <button class="hms-btn hms-btn-sm hms-btn-blue me-1" @click="openTransfer(a)" title="Transfer">
                      <i class="mdi mdi-bed-king"></i>
                    </button>
                    <button class="hms-btn hms-btn-sm hms-btn-amber" @click="openDischarge(a)" title="Discharge">
                      <i class="mdi mdi-exit-run"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="hms-empty">
            <i class="mdi mdi-account-injury"></i>No active admissions.
          </div>
        </div>
      </div>

      <!-- ============ ADD/EDIT WARD MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showWardForm" @click.self="closeWardForm">
        <div class="hms-modal" style="max-width:560px;">
          <div class="hms-modal-title">
            <span><i class="mdi" :class="editingWard ? 'mdi-pencil' : 'mdi-plus-circle'"></i> {{ editingWard ? 'Edit Ward' : 'Add Ward' }}</span>
            <button class="hms-modal-close" @click="closeWardForm">&times;</button>
          </div>
          <div v-if="wardError" class="hms-alert hms-alert-danger">
            <i class="mdi mdi-alert-circle me-1"></i>{{ wardError }}
          </div>

          <div class="row g-3">
            <div class="col-md-8"><label class="hms-label">Ward Name *</label>
              <input class="hms-input" v-model="wardForm.ward_name" placeholder="e.g. Maternity Ward" />
            </div>
            <div class="col-md-4"><label class="hms-label">Code</label>
              <input class="hms-input" v-model="wardForm.ward_code" placeholder="MAT" />
            </div>
            <div class="col-md-6"><label class="hms-label">Type</label>
              <select class="hms-input" v-model="wardForm.ward_type">
                <option value="general">General</option>
                <option value="icu">ICU</option>
                <option value="nicu">NICU</option>
                <option value="maternity">Maternity</option>
                <option value="surgical">Surgical</option>
                <option value="paediatric">Paediatric</option>
                <option value="isolation">Isolation</option>
                <option value="emergency">Emergency</option>
                <option value="psychiatric">Psychiatric</option>
                <option value="oncology">Oncology</option>
                <option value="renal">Renal</option>
              </select>
            </div>
            <div class="col-md-3"><label class="hms-label">Floor</label>
              <input type="number" class="hms-input" v-model.number="wardForm.floor_number" min="0" />
            </div>
            <div class="col-md-3"><label class="hms-label">Gender</label>
              <select class="hms-input" v-model="wardForm.gender_restriction">
                <option value="mixed">Mixed</option>
                <option value="male_only">Male only</option>
                <option value="female_only">Female only</option>
                <option value="paediatric">Paediatric</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Description</label>
              <textarea class="hms-input" rows="2" v-model="wardForm.description" placeholder="Optional description"></textarea>
            </div>
            <div v-if="editingWard" class="col-md-6"><label class="hms-label">Status</label>
              <select class="hms-input" v-model="wardForm.status">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="under_maintenance">Under Maintenance</option>
              </select>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100 mt-3" @click="saveWard" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : (editingWard ? 'Save Changes' : 'Create Ward') }}
          </button>
        </div>
      </div>

      <!-- ============ ADD BED MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showBedForm" @click.self="showBedForm=false">
        <div class="hms-modal" style="max-width:480px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-bed-empty me-2"></i>Add Bed</span>
            <button class="hms-modal-close" @click="showBedForm=false">&times;</button>
          </div>
          <div v-if="bedError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ bedError }}</div>

          <div class="mb-3"><label class="hms-label">Ward *</label>
            <select class="hms-input" v-model="bedForm.ward_id">
              <option value="">Select ward...</option>
              <option v-for="w in wards" :key="w.ward_id" :value="w.ward_id">{{ w.ward_name }}</option>
            </select>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-6"><label class="hms-label">Bed Number *</label>
              <input class="hms-input" v-model="bedForm.bed_number" placeholder="e.g. B-12" />
            </div>
            <div class="col-6"><label class="hms-label">Type</label>
              <select class="hms-input" v-model="bedForm.bed_type">
                <option value="standard">Standard</option>
                <option value="icu">ICU</option>
                <option value="isolation">Isolation</option>
                <option value="maternity">Maternity</option>
                <option value="paediatric">Paediatric</option>
                <option value="bariatric">Bariatric</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Demographic category</label>
              <select class="hms-input" v-model="bedForm.bed_category">
                <option value="mixed">Mixed</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="children">Children (under 12)</option>
                <option value="aged">Aged (≥65)</option>
                <option value="maternity">Maternity</option>
              </select>
            </div>
          </div>
          <div class="hms-section-label mb-2">EQUIPMENT</div>
          <div class="d-flex flex-wrap gap-3 mb-3">
            <label class="hms-check"><input type="checkbox" v-model="bedForm.has_oxygen"     :true-value="1" :false-value="0" /> Oxygen</label>
            <label class="hms-check"><input type="checkbox" v-model="bedForm.has_suction"    :true-value="1" :false-value="0" /> Suction</label>
            <label class="hms-check"><input type="checkbox" v-model="bedForm.has_ventilator" :true-value="1" :false-value="0" /> Ventilator</label>
            <label class="hms-check"><input type="checkbox" v-model="bedForm.has_monitor"    :true-value="1" :false-value="0" /> Monitor</label>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="saveBed" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : 'Create Bed' }}
          </button>
        </div>
      </div>

      <!-- ============ BED DETAIL / ACTIONS MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showBedDetail" @click.self="showBedDetail=false">
        <div class="hms-modal" style="max-width:520px;" v-if="selectedBed">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-bed me-2"></i>Bed {{ selectedBed.bed_number }}</span>
            <button class="hms-modal-close" @click="showBedDetail=false">&times;</button>
          </div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Ward</span><span>{{ selectedWard.ward_name }}</span></div>
            <div><span class="hms-kv-key">Type</span><span>{{ formatBedType(selectedBed.bed_type) }}</span></div>
            <div><span class="hms-kv-key">Status</span>
              <span><span class="hms-badge" :class="bedStatusBadge(selectedBed.status)">{{ formatBedStatus(selectedBed.status) }}</span></span>
            </div>
            <div v-if="selectedBed.patient_name"><span class="hms-kv-key">Patient</span><span>{{ selectedBed.patient_name }} ({{ selectedBed.patient_no }})</span></div>
            <div v-if="selectedBed.admission_date"><span class="hms-kv-key">Admitted</span><span>{{ formatDate(selectedBed.admission_date) }}</span></div>
          </div>

          <div class="d-flex flex-column gap-2">
            <button v-if="selectedBed.status === 'available'" class="hms-btn hms-btn-primary" @click="openAdmit(selectedBed)">
              <i class="mdi mdi-account-plus me-1"></i>Admit Patient to this Bed
            </button>
            <button v-if="selectedBed.status === 'occupied' && selectedBed.admission_id"
                    class="hms-btn hms-btn-amber"
                    @click="openDischargeFromBed">
              <i class="mdi mdi-exit-run me-1"></i>Discharge Patient
            </button>
            <button v-if="selectedBed.status === 'occupied' && selectedBed.admission_id"
                    class="hms-btn hms-btn-blue"
                    @click="openTransferFromBed">
              <i class="mdi mdi-bed-king me-1"></i>Transfer Patient
            </button>
            <button v-if="selectedBed.status === 'cleaning'"
                    class="hms-btn hms-btn-primary"
                    @click="markCleaned" :disabled="processing">
              <i class="mdi mdi-check me-1"></i>Mark as Clean & Available
            </button>
            <button v-if="selectedBed.status === 'occupied'" class="hms-btn hms-btn-blue" @click="requestCleaning" :disabled="processing">
              <i class="mdi mdi-broom me-1"></i>Request Cleaning
            </button>

            <div class="hms-section-label mt-3">CHANGE STATUS</div>
            <div class="d-flex flex-wrap gap-1">
              <button v-for="s in bedStatuses" :key="s"
                      class="hms-btn hms-btn-sm"
                      :class="{ 'hms-btn-primary': s === selectedBed.status, 'hms-btn-blue': s !== selectedBed.status }"
                      :disabled="processing"
                      @click="changeBedStatus(s)">
                {{ formatBedStatus(s) }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ ADMIT MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showAdmit" @click.self="showAdmit=false">
        <div class="hms-modal" v-if="admitForm.bed_id">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-account-plus me-2"></i>Admit Patient</span>
            <button class="hms-modal-close" @click="showAdmit=false">&times;</button>
          </div>
          <div v-if="admitError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ admitError }}</div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Ward / Bed</span><span>{{ admitForm.ward_name }} · Bed {{ admitForm.bed_number }}</span></div>
          </div>

          <div class="mb-3">
            <label class="hms-label">Find Patient *</label>
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input
                class="hms-search"
                v-model="patientSearch"
                placeholder="Type name, patient no, phone, or ID..."
                @input="onPatientSearch"
              />
            </div>
            <div v-if="patientResults.length" class="hms-suggest">
              <div v-for="pp in patientResults" :key="pp.patient_id" class="hms-suggest-item" @click="pickPatient(pp)">
                <div class="hms-text fw-semibold">{{ pp.first_name }} {{ pp.last_name }}</div>
                <div class="hms-muted-sm">{{ pp.patient_no }} · {{ pp.phone || '—' }} · {{ pp.gender || '—' }}</div>
              </div>
            </div>
            <div v-if="admitForm.patient_id" class="hms-picked mt-2">
              <i class="mdi mdi-check-circle me-1"></i>
              {{ admitForm.patient_name }} <span class="hms-muted-sm ms-1">({{ admitForm.patient_no }})</span>
              <button class="hms-modal-close ms-auto" @click="clearPickedPatient" title="Clear">&times;</button>
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Admission Type *</label>
              <select class="hms-input" v-model="admitForm.admission_type">
                <option value="elective">Elective</option>
                <option value="emergency">Emergency</option>
                <option value="referral">Referral</option>
                <option value="transfer">Transfer</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Expected Discharge</label>
              <input type="date" class="hms-input" v-model="admitForm.expected_discharge_date" />
            </div>
            <div class="col-12"><label class="hms-label">Diagnosis on Admission</label>
              <textarea class="hms-input" rows="3" v-model="admitForm.diagnosis_on_admission" placeholder="Working diagnosis"></textarea>
            </div>
          </div>

          <div class="d-flex justify-content-center mt-2">
            <SaveAndQueue ref="sqAdmit" from-point="admission" :saving="processing" :disabled="!admitForm.patient_id"
                          save-label="Admit Patient" saving-label="Admitting..."
                          btn-class="hms-btn hms-btn-primary" icon="mdi-account-plus" @save="doAdmit" />
          </div>
        </div>
      </div>

      <!-- ============ DISCHARGE MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showDischarge" @click.self="showDischarge=false">
        <div class="hms-modal" style="max-width:540px;" v-if="dischargeForm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-exit-run me-2"></i>Discharge Patient</span>
            <button class="hms-modal-close" @click="showDischarge=false">&times;</button>
          </div>
          <div v-if="dischargeError" class="hms-alert hms-alert-danger">{{ dischargeError }}</div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Patient</span><span>{{ dischargeForm.patient_name }} ({{ dischargeForm.patient_no }})</span></div>
            <div><span class="hms-kv-key">Admission</span><span>{{ dischargeForm.admission_number }}</span></div>
          </div>

          <div class="mb-3"><label class="hms-label">Discharge Diagnosis</label>
            <textarea class="hms-input" rows="2" v-model="dischargeForm.discharge_diagnosis"></textarea>
          </div>
          <div class="mb-3"><label class="hms-label">Discharge Summary</label>
            <textarea class="hms-input" rows="3" v-model="dischargeForm.discharge_summary"></textarea>
          </div>
          <div class="mb-3"><label class="hms-label">Condition</label>
            <select class="hms-input" v-model="dischargeForm.discharge_condition">
              <option value="improved">Improved</option>
              <option value="same">Same</option>
              <option value="deteriorated">Deteriorated</option>
              <option value="deceased">Deceased</option>
            </select>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="doDischarge" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Discharging...' : 'Discharge Patient' }}
          </button>
        </div>
      </div>

      <!-- ============ TRANSFER MODAL ============ -->
      <div class="hms-modal-overlay" v-if="showTransfer" @click.self="showTransfer=false">
        <div class="hms-modal" style="max-width:540px;" v-if="transferForm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-bed-king me-2"></i>Transfer Patient</span>
            <button class="hms-modal-close" @click="showTransfer=false">&times;</button>
          </div>
          <div v-if="transferError" class="hms-alert hms-alert-danger">{{ transferError }}</div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Patient</span><span>{{ transferForm.patient_name }}</span></div>
            <div><span class="hms-kv-key">From</span><span>{{ transferForm.from_ward_name }} · Bed {{ transferForm.from_bed_number }}</span></div>
          </div>

          <div class="mb-3"><label class="hms-label">Destination Bed *</label>
            <select class="hms-input" v-model="transferForm.to_bed_id">
              <option value="">Select an available bed...</option>
              <option v-for="b in availableBedsForTransfer" :key="b.bed_id" :value="b.bed_id">
                {{ b.ward_name }} · Bed {{ b.bed_number }} ({{ formatBedType(b.bed_type) }})
              </option>
            </select>
          </div>
          <div class="mb-3"><label class="hms-label">Reason</label>
            <textarea class="hms-input" rows="2" v-model="transferForm.reason"></textarea>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="doTransfer" :disabled="processing || !transferForm.to_bed_id">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Transferring...' : 'Transfer Patient' }}
          </button>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:          #f0f0f0;
  --hms-s:          #a0a0a0;
  --hms-m:          #808080;
  --hms-bg1:        #141414;
  --hms-bg2:        #0a0a0a;
  --hms-bg3:        #1e1e1e;
  --hms-bg4:        #0f0f0f;
  --hms-br:         #2a2a2a;
  --hms-input-br:   #333333;
  --hms-placeholder:#4a4a4a;
  --hms-primary:    #1a7fe8;
  --hms-primary-lt: rgba(26,127,232,0.12);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
}
html[data-theme="light"] {
  --hms-h:          #1a1a1a;
  --hms-s:          #555555;
  --hms-m:          #777777;
  --hms-bg1:        #ffffff;
  --hms-bg2:        #f4f6f9;
  --hms-bg3:        #eef1f5;
  --hms-bg4:        #ffffff;
  --hms-br:         #dee2e8;
  --hms-input-br:   #ced4da;
  --hms-placeholder:#aaaaaa;
  --hms-primary:    #0047AB;
  --hms-primary-lt: rgba(0,71,171,0.08);
  --hms-accent:     #F5821F;
  --hms-success:    #28a745;
  --hms-danger:     #dc3545;
  --hms-warning:    #ffc107;
  --hms-info:       #17a2b8;
}
html[data-theme="light"] body,
html[data-theme="dark"] body {
  background: var(--hms-bg2) !important;
  color: var(--hms-h) !important;
}
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted     { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm  { color: var(--hms-s); font-size: 12px; }
.hms-text      { color: var(--hms-h); }
.hms-text-success { color: var(--hms-success); }
.hms-text-info    { color: var(--hms-primary); }
.hms-mono      { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; color: var(--hms-h); }

/* Stat row */
.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 150px; cursor: default; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-green  { color: var(--hms-success); }
.hms-stat-amber  { color: #f1b44c; }
.hms-stat-info   { color: var(--hms-primary); }
.hms-stat-purple { color: #9b59b6; }

/* Tabs */
.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab {
  background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m);
  border-radius: 50px; padding: 7px 16px;
  font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s;
  display: inline-flex; align-items: center;
}
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }
.hms-tab-count { background: var(--hms-primary); color: #fff; border-radius: 999px; padding: 1px 7px; font-size: 10px; margin-left: 8px; }

/* Card */
.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; margin-bottom: 16px; }

/* Spinner / empty */
.hms-spinner { display: flex; justify-content: center; padding: 60px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

/* Ward block / bed grid */
.hms-ward-block { padding: 16px 18px; }
.hms-ward-title { color: var(--hms-h); font-size: 16px; font-weight: 700; display: flex; align-items: center; }

.hms-bed-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}
.hms-bed-cell {
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: all .15s;
  border: 1px solid var(--hms-br);
  background: var(--hms-bg2);
  position: relative;
  min-height: 90px;
}
.hms-bed-cell:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,.12); }
.hms-bed-num    { font-weight: 800; color: var(--hms-h); font-size: 14px; }
.hms-bed-status { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; margin-top: 2px; }
.hms-bed-patient { font-size: 12px; margin-top: 6px; color: var(--hms-h); }
.hms-bed-icons { position: absolute; top: 8px; right: 8px; display: flex; gap: 4px; }
.hms-bed-icons i { font-size: 14px; color: var(--hms-s); }

.hms-bed-available { background: rgba(40,167,69,.08); border-color: rgba(40,167,69,.4); }
.hms-bed-available .hms-bed-status { color: var(--hms-success); }

.hms-bed-occupied { background: var(--hms-primary-lt); border-color: rgba(26,127,232,.4); }
.hms-bed-occupied .hms-bed-status { color: var(--hms-primary); }

.hms-bed-cleaning { background: rgba(245,158,11,.08); border-color: rgba(245,158,11,.4); }
.hms-bed-cleaning .hms-bed-status { color: #f1b44c; }

.hms-bed-reserved { background: rgba(155,89,182,.08); border-color: rgba(155,89,182,.4); }
.hms-bed-reserved .hms-bed-status { color: #9b59b6; }

.hms-bed-maintenance { background: var(--hms-bg3); border-color: var(--hms-br); }
.hms-bed-maintenance .hms-bed-status { color: var(--hms-s); }

/* Table */
.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }

/* Badges */
.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15);  color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15);  color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }

/* Buttons */
.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue   { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber  { background: rgba(245,158,11,.15);  color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-red    { background: rgba(220,53,69,.15);   color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

/* Form bits */
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; transition: border-color .15s; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 8px; }
.hms-check { display: inline-flex; align-items: center; gap: 6px; color: var(--hms-h); font-size: 13px; cursor: pointer; }
.hms-check input { accent-color: var(--hms-primary); }

/* Search wrap */
.hms-search-wrap { position: relative; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-suggest { background: var(--hms-bg4); border: 1px solid var(--hms-br); border-radius: 8px; margin-top: 6px; max-height: 200px; overflow-y: auto; }
.hms-suggest-item { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); }
.hms-suggest-item:last-child { border-bottom: none; }
.hms-suggest-item:hover { background: var(--hms-primary-lt); }
.hms-picked { background: rgba(40,167,69,.08); border: 1px solid rgba(40,167,69,.3); border-radius: 8px; padding: 8px 12px; color: var(--hms-success); font-size: 13px; display: flex; align-items: center; }

/* Modal */
.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; margin-left: auto; }
.hms-modal-close:hover { color: var(--hms-h); }

/* KV */
.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 140px; font-weight: 600; }

/* Alerts */
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger  { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

/* Toast */
.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }

@media (max-width: 768px) {
  .hms-stat-row .hms-stat { min-width: calc(50% - 6px); }
  .hms-bed-grid { grid-template-columns: repeat(2, 1fr); }
  .hms-modal { padding: 18px; }
}
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import SaveAndQueue from "@/components/hms-shared/SaveAndQueue.vue";
import { executeJson, hms } from "@/api";
import { useI18n } from "@/api/i18n";

export default {
  name: "WardsAndBeds",
  components: { Navigation, SaveAndQueue },
  data() {
    return {
      loading: true,
      processing: false,
      tab: "map",
      i18n: useI18n(),

      bedMap: [],
      wards:  [],
      beds:   [],
      activeAdmissions: [],

      bedStatuses: ['available','occupied','cleaning','reserved','maintenance'],

      // Add/edit ward
      showWardForm: false,
      editingWard:  false,
      wardError:    "",
      wardForm: this.emptyWardForm(),

      // Add bed
      showBedForm: false,
      bedError:    "",
      bedForm: this.emptyBedForm(),

      // Bed detail
      showBedDetail: false,
      selectedBed:   null,
      selectedWard:  null,

      // Admit
      showAdmit:       false,
      admitError:      "",
      admitForm:       null,
      patientSearch:   "",
      patientSearchTimer: null,
      patientResults:  [],

      // Discharge
      showDischarge:   false,
      dischargeError:  "",
      dischargeForm:   null,

      // Transfer
      showTransfer:    false,
      transferError:   "",
      transferForm:    null,
      availableBeds:   [],

      // Toast
      toastMsg:    "",
      toastError:  false,
      toastTimer:  null,

      branchId:   sessionStorage.getItem("branchId"),
      hospitalId: sessionStorage.getItem("hospitalId")
    };
  },
  computed: {
    groupedMap() {
      var groups = {};
      this.bedMap.forEach(function(row) {
        if (!row.ward_id) return;
        if (!groups[row.ward_id]) {
          groups[row.ward_id] = {
            ward_id:            row.ward_id,
            ward_name:          row.ward_name,
            ward_type:          row.ward_type,
            floor_number:       row.floor_number,
            gender_restriction: row.gender_restriction,
            beds: []
          };
        }
        if (row.bed_id) groups[row.ward_id].beds.push(row);
      });
      return Object.values(groups).sort(function(a,b){
        return (a.floor_number||0) - (b.floor_number||0) || a.ward_name.localeCompare(b.ward_name);
      });
    },
    totals() {
      var b = 0, av = 0, oc = 0, cl = 0;
      this.bedMap.forEach(function(r) {
        if (!r.bed_id) return;
        b++;
        if (r.status === 'available') av++;
        else if (r.status === 'occupied') oc++;
        else if (r.status === 'cleaning') cl++;
      });
      return { beds: b, available: av, occupied: oc, cleaning: cl };
    },
    availableBedsForTransfer() {
      if (!this.transferForm) return [];
      var fromBed = this.transferForm.from_bed_id;
      return this.availableBeds.filter(function(b) { return b.bed_id != fromBed; });
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) {
      this.$router.push("/login");
      return;
    }
    this.applyTheme();
    this.loadAll();
  },
  beforeUnmount() {
    if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    t(key, fallback) {
      return (this.i18n && this.i18n.state.strings[key]) || fallback || key;
    },
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    emptyWardForm() {
      return { ward_id:null, ward_name:"", ward_code:"", ward_type:"general", floor_number:1, gender_restriction:"mixed", description:"", status:"active" };
    },
    emptyBedForm() {
      return { ward_id:"", bed_number:"", bed_type:"standard", has_oxygen:0, has_suction:0, has_ventilator:0, has_monitor:0 };
    },
    formatWardType(t) {
      var map = { general:'General', icu:'ICU', nicu:'NICU', maternity:'Maternity', surgical:'Surgical', paediatric:'Paediatric', isolation:'Isolation', emergency:'Emergency', psychiatric:'Psychiatric', oncology:'Oncology', renal:'Renal' };
      return map[t] || (t || '').toString();
    },
    formatBedType(t) {
      var map = { standard:'Standard', icu:'ICU', isolation:'Isolation', maternity:'Maternity', paediatric:'Paediatric', bariatric:'Bariatric' };
      return map[t] || (t || 'standard');
    },
    formatBedStatus(s) {
      var map = { available:'Available', occupied:'Occupied', cleaning:'Cleaning', reserved:'Reserved', maintenance:'Maintenance' };
      return map[s] || s;
    },
    formatGender(g) {
      var map = { mixed:'Mixed', male_only:'Male', female_only:'Female', paediatric:'Paediatric' };
      return map[g] || g;
    },
    formatDate(d) {
      if (!d) return "—";
      try {
        var dt = new Date(d);
        if (isNaN(dt.getTime())) return d;
        return dt.toLocaleString(undefined, { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
      } catch(e) { return d; }
    },
    bedClass(s) {
      return 'hms-bed-' + (s || 'available');
    },
    bedStatusBadge(s) {
      switch(s) {
        case 'available':   return 'hms-badge-success';
        case 'occupied':    return 'hms-badge-info';
        case 'cleaning':    return 'hms-badge-warning';
        case 'reserved':    return 'hms-badge-info';
        case 'maintenance': return 'hms-badge-neutral';
        default:            return 'hms-badge-neutral';
      }
    },
    admTypeBadge(t) {
      switch(t) {
        case 'emergency': return 'hms-badge-danger';
        case 'elective':  return 'hms-badge-info';
        case 'referral':  return 'hms-badge-warning';
        default:          return 'hms-badge-neutral';
      }
    },
    showToast(msg, isError) {
      this.toastMsg = msg;
      this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(function(){ self.toastMsg = ""; }, 2800);
    },

    async loadAll() {
      this.loading = true;
      try {
        await Promise.all([this.loadBedMap(), this.loadWards(), this.loadAdmissions()]);
      } catch (e) {
        console.error(e);
      }
      this.loading = false;
    },
    async loadBedMap() {
      try {
        var r = await executeJson({ function: "getBedMap", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.bedMap = (r.data.data && r.data.data.BedMap) || [];
      } catch (e) { console.error(e); }
    },
    async loadWards() {
      try {
        var r = await executeJson({ function: "getWards", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.wards = (r.data.data && r.data.data.Wards) || [];
      } catch (e) { console.error(e); }
    },
    async loadAdmissions() {
      try {
        var r = await executeJson({ function: "getActiveAdmissions", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.activeAdmissions = (r.data.data && r.data.data.Admissions) || [];
      } catch (e) { console.error(e); }
    },
    async loadAvailableBeds() {
      try {
        var r = await executeJson({ function: "getAvailableBeds", branch_id: this.branchId }, hms);
        if (r.data && r.data.success) this.availableBeds = (r.data.data && r.data.data.Beds) || [];
      } catch (e) { console.error(e); }
    },

    // ====== WARD ======
    openAddWard() {
      this.editingWard = false;
      this.wardForm    = this.emptyWardForm();
      this.wardError   = "";
      this.showWardForm = true;
    },
    openEditWard(w) {
      this.editingWard = true;
      this.wardForm    = Object.assign(this.emptyWardForm(), {
        ward_id:            w.ward_id,
        ward_name:          w.ward_name || "",
        ward_code:          w.ward_code || "",
        ward_type:          w.ward_type || "general",
        floor_number:       w.floor_number || 1,
        gender_restriction: w.gender_restriction || "mixed",
        description:        w.description || "",
        status:             w.status || "active"
      });
      this.wardError    = "";
      this.showWardForm = true;
    },
    closeWardForm() { this.showWardForm = false; },
    async saveWard() {
      this.wardError = "";
      if (!this.wardForm.ward_name) { this.wardError = "Ward name is required."; return; }
      this.processing = true;
      try {
        var fn = this.editingWard ? "updateWard" : "createWard";
        var r  = await executeJson(Object.assign({ function: fn }, this.wardForm), hms);
        if (r.data && r.data.success) {
          this.showToast(this.editingWard ? "Ward updated" : "Ward created");
          this.showWardForm = false;
          await Promise.all([this.loadBedMap(), this.loadWards()]);
        } else {
          this.wardError = r.data.message || "Operation failed.";
        }
      } catch (e) {
        this.wardError = "Connection error.";
      }
      this.processing = false;
    },

    // ====== BED ======
    openAddBed(wardId) {
      this.bedForm  = Object.assign(this.emptyBedForm(), { ward_id: wardId || (this.wards[0] && this.wards[0].ward_id) || "" });
      this.bedError = "";
      this.showBedForm = true;
    },
    async saveBed() {
      this.bedError = "";
      if (!this.bedForm.ward_id || !this.bedForm.bed_number) { this.bedError = "Ward and bed number are required."; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "createBed" }, this.bedForm), hms);
        if (r.data && r.data.success) {
          this.showToast("Bed created");
          this.showBedForm = false;
          await Promise.all([this.loadBedMap(), this.loadWards()]);
        } else {
          this.bedError = r.data.message || "Failed to create bed.";
        }
      } catch (e) {
        this.bedError = "Connection error.";
      }
      this.processing = false;
    },
    openBedDetail(bed, ward) {
      this.selectedBed  = bed;
      this.selectedWard = ward;
      this.showBedDetail = true;
    },
    async changeBedStatus(status) {
      if (!this.selectedBed) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "updateBedStatus", bed_id: this.selectedBed.bed_id, status: status }, hms);
        if (r.data && r.data.success) {
          this.showToast("Bed status updated");
          this.showBedDetail = false;
          await this.loadBedMap();
        } else {
          this.showToast(r.data.message || "Update failed", true);
        }
      } catch (e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async requestCleaning() {
      if (!this.selectedBed) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "requestBedCleaning", bed_id: this.selectedBed.bed_id, ward_id: this.selectedWard.ward_id }, hms);
        if (r.data && r.data.success) {
          this.showToast("Cleaning requested");
          this.showBedDetail = false;
          await this.loadBedMap();
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch (e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async markCleaned() {
      if (!this.selectedBed) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "markBedCleaned", bed_id: this.selectedBed.bed_id }, hms);
        if (r.data && r.data.success) {
          this.showToast("Bed marked clean");
          this.showBedDetail = false;
          await this.loadBedMap();
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch (e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    // ====== ADMIT ======
    openAdmit(bed) {
      this.admitError    = "";
      this.patientSearch = "";
      this.patientResults = [];
      this.admitForm = {
        bed_id:                  bed.bed_id,
        bed_number:              bed.bed_number,
        ward_id:                 this.selectedWard.ward_id,
        ward_name:               this.selectedWard.ward_name,
        patient_id:              null,
        patient_name:            "",
        patient_no:              "",
        admission_type:          "elective",
        diagnosis_on_admission:  "",
        expected_discharge_date: "",
        doctor_id:               sessionStorage.getItem("userId") || ""
      };
      this.showBedDetail = false;
      this.showAdmit = true;
    },
    onPatientSearch() {
      if (this.patientSearchTimer) clearTimeout(this.patientSearchTimer);
      var self = this;
      if (!this.patientSearch || this.patientSearch.length < 2) { this.patientResults = []; return; }
      this.patientSearchTimer = setTimeout(async function() {
        try {
          var r = await executeJson({ function: "searchPatients", q: self.patientSearch.trim() }, hms);
          if (r.data && r.data.success) {
            self.patientResults = (r.data.data && r.data.data.Patients) || [];
          }
        } catch (e) { /* silent */ }
      }, 300);
    },
    pickPatient(p) {
      this.admitForm.patient_id   = p.patient_id;
      this.admitForm.patient_name = (p.first_name + ' ' + p.last_name).trim();
      this.admitForm.patient_no   = p.patient_no;
      this.patientResults = [];
      this.patientSearch  = "";
    },
    clearPickedPatient() {
      this.admitForm.patient_id   = null;
      this.admitForm.patient_name = "";
      this.admitForm.patient_no   = "";
    },
    async doAdmit() {
      this.admitError = "";
      if (!this.admitForm.patient_id) { this.admitError = "Pick a patient first."; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "admitPatient" }, this.admitForm), hms);
        if (r.data && r.data.success) {
          this.showToast("Patient admitted");
          // If the admitter picked a "Save & queue to ___", route the patient now.
          var pid = this.admitForm && this.admitForm.patient_id;
          if (this.$refs.sqAdmit && pid) {
            var q = await this.$refs.sqAdmit.queueIfSelected(pid);
            if (q && q.ok) this.showToast("Queued to " + q.dept_name + " (ticket " + q.ticket_number + ")");
          }
          this.showAdmit = false;
          await Promise.all([this.loadBedMap(), this.loadAdmissions(), this.loadWards()]);
        } else { this.admitError = r.data.message || "Admit failed."; }
      } catch (e) { this.admitError = "Connection error."; }
      this.processing = false;
    },

    // ====== DISCHARGE ======
    openDischarge(adm) {
      this.dischargeError = "";
      this.dischargeForm = {
        admission_id:        adm.admission_id,
        admission_number:    adm.admission_number,
        bed_id:              adm.bed_id,
        patient_name:        adm.patient_name,
        patient_no:          adm.patient_no,
        discharge_diagnosis: "",
        discharge_summary:   "",
        discharge_condition: "improved"
      };
      this.showDischarge = true;
    },
    openDischargeFromBed() {
      if (!this.selectedBed || !this.selectedBed.admission_id) return;
      this.dischargeError = "";
      this.dischargeForm = {
        admission_id:        this.selectedBed.admission_id,
        admission_number:    "",
        bed_id:              this.selectedBed.bed_id,
        patient_name:        this.selectedBed.patient_name,
        patient_no:          this.selectedBed.patient_no,
        discharge_diagnosis: "",
        discharge_summary:   "",
        discharge_condition: "improved"
      };
      this.showBedDetail = false;
      this.showDischarge = true;
    },
    async doDischarge() {
      this.dischargeError = "";
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "dischargePatient" }, this.dischargeForm), hms);
        if (r.data && r.data.success) {
          this.showToast("Patient discharged");
          this.showDischarge = false;
          await Promise.all([this.loadBedMap(), this.loadAdmissions(), this.loadWards()]);
        } else { this.dischargeError = r.data.message || "Failed"; }
      } catch (e) { this.dischargeError = "Connection error."; }
      this.processing = false;
    },

    // ====== TRANSFER ======
    async openTransfer(adm) {
      this.transferError = "";
      this.transferForm = {
        admission_id:    adm.admission_id,
        patient_id:      adm.patient_id,
        patient_name:    adm.patient_name,
        from_bed_id:     adm.bed_id,
        from_bed_number: adm.bed_number,
        from_ward_id:    adm.ward_id,
        from_ward_name:  adm.ward_name,
        to_bed_id:       "",
        to_ward_id:      "",
        reason:          ""
      };
      await this.loadAvailableBeds();
      this.showTransfer = true;
    },
    async openTransferFromBed() {
      if (!this.selectedBed || !this.selectedBed.admission_id) return;
      this.transferError = "";
      this.transferForm = {
        admission_id:    this.selectedBed.admission_id,
        patient_id:      null,  // server lookup will resolve via admission
        patient_name:    this.selectedBed.patient_name,
        from_bed_id:     this.selectedBed.bed_id,
        from_bed_number: this.selectedBed.bed_number,
        from_ward_id:    this.selectedWard.ward_id,
        from_ward_name:  this.selectedWard.ward_name,
        to_bed_id:       "",
        to_ward_id:      "",
        reason:          ""
      };
      await this.loadAvailableBeds();
      this.showBedDetail = false;
      this.showTransfer = true;
    },
    async doTransfer() {
      this.transferError = "";
      if (!this.transferForm.to_bed_id) { this.transferError = "Pick a destination bed."; return; }
      // Resolve to_ward_id from chosen bed
      var dest = this.availableBeds.find(b => b.bed_id == this.transferForm.to_bed_id);
      if (dest) this.transferForm.to_ward_id = dest.ward_id;

      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "transferPatient" }, this.transferForm), hms);
        if (r.data && r.data.success) {
          this.showToast("Patient transferred");
          this.showTransfer = false;
          await Promise.all([this.loadBedMap(), this.loadAdmissions(), this.loadWards()]);
        } else { this.transferError = r.data.message || "Failed"; }
      } catch (e) { this.transferError = "Connection error."; }
      this.processing = false;
    }
  }
};
</script>
